import React from 'react';
import styled from 'styled-components';
import { ArrowRightToLine, ArrowRightFromLine } from 'lucide-react';
import { slateColors, fontFamily } from '../ContentPages/AppLandingPage/styles';
import * as Buttons from '../Controls/Buttons';
import * as TypographyStyles from '../TypographyStyles';
import { TimeContainer } from '../Elements/TimeComponents';
import { colors } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const FrameContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 95%;
  max-width: 800px;
  max-height: 90vh;
  position: relative;
  box-sizing: border-box;
  border: 1px solid ${slateColors[100]};
  overflow: hidden;
  
  @media (min-width: 640px) {
    padding: 2.5rem;
    width: 90%;
    max-width: 500px;
  }
  
  @media (min-width: 768px) {
    padding: 3rem;
    border-radius: 2rem;
    max-width: 500px;
  }
  
  @media (min-width: 1024px) {
    max-width: 500px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const StopsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  align-self: stretch;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  width: 100%;
`;

const StopItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem;
  border-radius: 1rem;
  background: ${slateColors[50]};
  border: 1px solid ${slateColors[200]};
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`;

const StopHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const StopTime = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  flex-shrink: 0;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.grey600};
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
  }
`;

const StopName = styled(TypographyStyles.BodyMSemibold)`
  color: ${slateColors[800]};
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

type Stopover = {
  stop: {
    name: string;
  };
  arrival: string | null;
  plannedArrival: string | null;
  arrivalDelay: number | null;
  arrivalPlatform: string | null;
  plannedArrivalPlatform: string | null;
  departure: string | null;
  plannedDeparture: string | null;
  departureDelay: number | null;
  departurePlatform: string | null;
  plannedDeparturePlatform: string | null;
  cancelled?: boolean;
};

interface StopoversCardProps {
  onClose: () => void;
  stopovers: Stopover[];
  originName: string;
  destinationName: string;
}

const StopoversCard: React.FC<StopoversCardProps> = ({ onClose, stopovers, originName, destinationName }) => {
  const { t } = useTranslation();
  
  // Filter out origin and destination, show only intermediate stops
  const intermediateStops = stopovers.filter((stopover, index) => {
    // First stopover is origin (no arrival), last is destination (no departure)
    return index > 0 && index < stopovers.length - 1;
  });

  return (
    <FrameContainer>
      <HeaderSection>
        <TypographyStyles.BodyLSemibold>{t('journeyDetails.stopovers.title')}</TypographyStyles.BodyLSemibold>
        <Buttons.CloseButton onClick={onClose} disabled={false} />
      </HeaderSection>

      <StopsList>
        {/* Origin */}
        {stopovers[0] && (() => {
          const originStopover = stopovers[0];
          const isDepartureCancelled = originStopover.cancelled === true || 
            !!(originStopover.plannedDeparture && !originStopover.departure && originStopover.departureDelay === null);
          
          return (
            <StopItem>
              <StopHeader>
                <StopTime>
                  <TimeRow>
                    <ArrowIcon>
                      <ArrowRightFromLine size={14} />
                    </ArrowIcon>
                    <TimeContainer 
                      time={originStopover.plannedDeparture || ''} 
                      delaySec={originStopover.departureDelay || undefined}
                      size={14}
                      cancelled={isDepartureCancelled}
                    />
                  </TimeRow>
                </StopTime>
                <StopName>{originName}</StopName>
              </StopHeader>
            </StopItem>
          );
        })()}

        {/* Intermediate stops */}
        {intermediateStops.map((stopover, index) => {
          // Check if arrival/departure are cancelled
          const isArrivalCancelled = stopover.cancelled === true || 
            !!(stopover.plannedArrival && !stopover.arrival && stopover.arrivalDelay === null);
          const isDepartureCancelled = stopover.cancelled === true || 
            !!(stopover.plannedDeparture && !stopover.departure && stopover.departureDelay === null);
          
          // Show times even if cancelled - always show if there's a planned time
          const hasArrival = stopover.plannedArrival != null;
          const hasDeparture = stopover.plannedDeparture != null;
          
          return (
            <StopItem key={index}>
              <StopHeader>
                <StopTime>
                  {hasArrival && (
                    <TimeRow>
                      <ArrowIcon>
                        <ArrowRightToLine size={14} />
                      </ArrowIcon>
                      <TimeContainer 
                        time={stopover.plannedArrival || ''} 
                        delaySec={stopover.arrivalDelay || undefined}
                        size={14}
                        cancelled={isArrivalCancelled}
                      />
                    </TimeRow>
                  )}
                  {hasDeparture && (
                    <TimeRow>
                      <ArrowIcon>
                        <ArrowRightFromLine size={14} />
                      </ArrowIcon>
                      <TimeContainer 
                        time={stopover.plannedDeparture || ''} 
                        delaySec={stopover.departureDelay || undefined}
                        size={14}
                        cancelled={isDepartureCancelled}
                      />
                    </TimeRow>
                  )}
                </StopTime>
                <StopName>{stopover.stop.name}</StopName>
              </StopHeader>
            </StopItem>
          );
        })}

        {/* Destination */}
        {stopovers[stopovers.length - 1] && (() => {
          const destinationStopover = stopovers[stopovers.length - 1];
          const isArrivalCancelled = destinationStopover.cancelled === true || 
            !!(destinationStopover.plannedArrival && !destinationStopover.arrival && destinationStopover.arrivalDelay === null);
          
          return (
            <StopItem>
              <StopHeader>
                <StopTime>
                  <TimeRow>
                    <ArrowIcon>
                      <ArrowRightToLine size={14} />
                    </ArrowIcon>
                    <TimeContainer 
                      time={destinationStopover.plannedArrival || ''} 
                      delaySec={destinationStopover.arrivalDelay || undefined}
                      size={14}
                      cancelled={isArrivalCancelled}
                    />
                  </TimeRow>
                </StopTime>
                <StopName>{destinationName}</StopName>
              </StopHeader>
            </StopItem>
          );
        })()}
      </StopsList>
    </FrameContainer>
  );
};

export default StopoversCard;

